
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { ClockIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import clsxm from '@/lib/clsxm';
import AuthLayout from '@/components/auth/AuthLayout';
import { describeAuthMessage } from '@/components/auth/authMessages';
import AuthTurnstile from '@/components/auth/AuthTurnstile';
import { AuthButton, AuthHeading, buttonClass, fieldClass, labelClass, linkClass, Notice, OrDivider, Spinner, StatusIcon, useAuthT, useAuthTrack, } from '@/components/auth/kit';
import { isTurnstileEnabled, TURNSTILE_ERROR_MESSAGE, type TurnstileHandle, } from '@/components/Turnstile';
import { createClient } from '@/utils/supabase/component';
// Landing page for failed OTP verification (~70 users/day). The dominant
// cause is a consumed/expired one-time token: the user clicked the email link
// twice, an inbox link-scanner burned it, or the link was old. In most of
// those cases the account is fine (often already verified) — so instead of a
// scary dead-end suggesting "sign up again", this page:
//   1. auto-redirects to the dashboard when a session already exists
//   2. leads with "sign in" (the account exists!)
//   3. offers a self-serve fresh-link resend
export default function AuthCodeError() {
    const router = useRouter();
    const { t } = useAuthT();
    const track = useAuthTrack('link_error');
    const supabase = createClient();
    // The confirm route forwards the failed OTP's type; recovery links need a
    // password-reset email, not a signup-confirmation resend.
    const isRecovery = router.query.type === 'recovery';
    const [checkingSession, setCheckingSession] = useState(true);
    const [email, setEmail] = useState('');
    const [resending, setResending] = useState(false);
    // Resend / password recovery are captcha-protected once Supabase Attack
    // Protection is on.
    const captchaRef = useRef<TurnstileHandle>(null);
    const [resendResult, setResendResult] = useState<{
        type: 'success' | 'info' | 'error';
        text: string;
    } | null>(null);
    // If the first click already verified + signed the user in, this second
    // visit shouldn't show an error at all.
    useEffect(() => {
        let cancelled = false;
        if (!router.isReady)
            return;
        if (isRecovery) {
            // A recovery visitor wants a fresh reset link, not the dashboard.
            setCheckingSession(false);
            return;
        }
        supabase.auth
            .getUser()
            .then(({ data }) => {
            if (cancelled)
                return;
            if (data.user) {
                router.replace('/dashboard');
            }
            else {
                setCheckingSession(false);
            }
        })
            .catch(() => {
            if (!cancelled)
                setCheckingSession(false);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady, isRecovery]);
    const handleResend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || resending)
            return;
        setResending(true);
        setResendResult(null);
        try {
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setResendResult({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
                return;
            }
            const { error } = isRecovery
                ? await supabase.auth.resetPasswordForEmail(email.trim(), {
                    redirectTo: `${window.location.origin}/auth/confirm?type=recovery&next=/auth/reset-password`,
                    captchaToken,
                })
                : await supabase.auth.resend({
                    type: 'signup',
                    email: email.trim(),
                    options: {
                        emailRedirectTo: `${window.location.origin}/auth/confirm`,
                        captchaToken,
                    },
                });
            if (!error) {
                setResendResult({
                    type: 'success',
                    text: isRecovery
                        ? 'A fresh password reset link is on its way. Check your inbox.'
                        : 'A fresh confirmation link is on its way. Check your inbox.',
                });
            }
            else if (/already confirmed|already been confirmed/i.test(error.message)) {
                // Best outcome: nothing was wrong — the earlier click verified them.
                setResendResult({
                    type: 'info',
                    text: 'Good news: this email is already verified. Just sign in.',
                });
            }
            else {
                setResendResult({ type: 'error', text: error.message });
            }
        }
        catch {
            setResendResult({
                type: 'error',
                text: 'Could not resend the email. Please try again.',
            });
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            captchaRef.current?.reset();
            setResending(false);
        }
    };
    const mode = isRecovery ? 'reset' : 'resend';
    const result = resendResult ? describeAuthMessage(resendResult.text) : null;
    useEffect(() => {
        if (resendResult?.type === 'error') {
            track.error({
                mode,
                reason: describeAuthMessage(resendResult.text).reason,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resendResult, track]);
    return (<AuthLayout title={t('Link expired')}>
      {checkingSession ? (<div role='status' className='flex justify-center py-16'>
          <Spinner className='h-7 w-7 text-primary-600 dark:text-primary-400'/>
          <span className='sr-only'>{t('Checking your link...')}</span>
        </div>) : (<div>
          <AuthHeading icon={<StatusIcon kind='warning'>
                <ClockIcon />
              </StatusIcon>} title={t('This link has expired or was already used')}>
            <p>
              {isRecovery
                ? t('No worries: password reset links work once and expire quickly. Request a fresh one below.')
                : t('No worries, your account is safe. If you already clicked the link once, your email is probably verified and you can simply sign in.')}
            </p>
          </AuthHeading>

          <div className='mt-7'>
            <Link href='/login' className={buttonClass('primary')}>
              {t('Sign in')}
            </Link>
          </div>

          <div className='my-6'>
            <OrDivider label={t('or get a fresh link')}/>
          </div>

          <form onSubmit={(event) => {
                track.started({
                    mode,
                    method: isRecovery ? 'reset_link' : 'resend',
                });
                return handleResend(event);
            }}>
            <label htmlFor='email' className={labelClass}>
              {t('Email address')}
            </label>
            <input id='email' name='email' type='email' inputMode='email' autoComplete='email' autoCapitalize='none' autoCorrect='off' spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)} placeholder='name@example.com' className={clsxm(fieldClass, 'mt-2')}/>
            <div className='mt-5'>
              <AuthTurnstile ref={captchaRef} action={isRecovery ? 'password-reset' : 'resend'} className='mb-4'/>
              {resendResult && result && (<Notice kind={resendResult.type} className='mb-4' action={resendResult.type === 'info' ? (<Link href='/login' className={clsxm(linkClass, 'text-[14px]')}>
                        {t('Sign in')}
                      </Link>) : undefined}>
                  {t(result.key, result.values)}
                </Notice>)}
              <AuthButton type='submit' variant='secondary' loading={resending} disabled={resending}>
                {resending
                ? t('Sending...')
                : isRecovery
                    ? t('Email me a new password reset link')
                    : t('Email me a new confirmation link')}
              </AuthButton>
            </div>
          </form>

          {!isRecovery && (<div className='mt-8 text-center text-[14px]'>
              <Link href='/auth/forgot-password' className={linkClass}>
                {t('Forgot your password?')}
              </Link>
            </div>)}
        </div>)}
    </AuthLayout>);
}

    async function __Next_Translate__getStaticProps__1a168f61576__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/auth/auth-code-error',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a168f61576__ as getStaticProps }
  